// Depth for splash particles: above target sprites
import { playSound } from "../utils/sound-utils";

const SPLASH_PARTICLE_DEPTH = 20000;
/**
 * Triggers a gray splash particle effect at (x, y) in the given Phaser scene.
 * Uses a mix of light and dark gray particles, lasting 0.2 seconds.
 */
export function showGraySplashParticle(
  scene: Phaser.Scene,
  x: number,
  y: number
) {
  // Create two tint frames for light and dark gray if not present
  if (!scene.textures.exists("lightGray")) {
    const graphics = scene.add.graphics();
    graphics.fillStyle(0xcccccc, 1); // light gray
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("lightGray", 8, 8);
    graphics.clear();
    graphics.fillStyle(0x444444, 1); // dark gray
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("darkGray", 8, 8);
    graphics.destroy();
  }
  // Create light gray emitter
  const lightEmitter = scene.add.particles(x, y, "lightGray", {
    lifespan: 200,
    speed: { min: 60, max: 120 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0 },
    quantity: 6,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 0,
  });
  // Create dark gray emitter
  const darkEmitter = scene.add.particles(x, y, "darkGray", {
    lifespan: 200,
    speed: { min: 60, max: 120 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0 },
    quantity: 6,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 0,
  });

  // Ensure particles are only visible in the main camera, not the UI camera
  const cameras = scene.cameras.cameras;
  cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(lightEmitter);
      camera.ignore(darkEmitter);
    }
  });

  if (scene.sys.displayList) {
    scene.sys.displayList.bringToTop(lightEmitter);
    scene.sys.displayList.bringToTop(darkEmitter);
  }
  // Remove emitters after 0.2 seconds
  scene.time.delayedCall(200, () => {
    lightEmitter.destroy();
    darkEmitter.destroy();
  });
}
// SplashParticle.ts
// Simple splash particle effect for hit events

/**
 * Triggers a splash particle effect at (x, y) in the given Phaser scene.
 * Uses a mix of bright and dark red particles, lasting 0.2 seconds.
 */
export function showSplashParticle(scene: Phaser.Scene, x: number, y: number) {
  // Create two tint frames for bright and dark red if not present
  if (!scene.textures.exists("brightRed")) {
    const graphics = scene.add.graphics();
    graphics.fillStyle(0xff0000, 1); // bright red
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("brightRed", 8, 8);
    graphics.clear();
    graphics.fillStyle(0x800000, 1); // dark red
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("darkRed", 8, 8);
    graphics.destroy();
  }
  // Create bright red emitter
  const brightEmitter = scene.add.particles(x, y, "brightRed", {
    lifespan: 200,
    speed: { min: 60, max: 120 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0 },
    quantity: 6,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 0,
  });
  brightEmitter.setDepth(SPLASH_PARTICLE_DEPTH);
  // Create dark red emitter
  const darkEmitter = scene.add.particles(x, y, "darkRed", {
    lifespan: 200,
    speed: { min: 60, max: 120 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0 },
    quantity: 6,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 0,
  });
  darkEmitter.setDepth(SPLASH_PARTICLE_DEPTH);

  // Ensure particles are only visible in the main camera, not the UI camera
  const cameras = scene.cameras.cameras;
  cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(brightEmitter);
      camera.ignore(darkEmitter);
    }
  });

  // Remove emitters after 0.2 seconds
  scene.time.delayedCall(200, () => {
    brightEmitter.destroy();
    darkEmitter.destroy();
  });
}

/**
 * Triggers a beam up particle effect at (x, y) in the given Phaser scene.
 * Creates a vertical beam of light particles rising upward with a glow effect.
 */
export function showBeamUpParticle(scene: Phaser.Scene, x: number, y: number) {
  // Play teleport sound effect
  playSound(scene, "teleport-appear");

  // Create beam particle texture if not present
  if (!scene.textures.exists("beamParticle")) {
    const graphics = scene.add.graphics();
    graphics.fillStyle(0x00ffff, 1); // cyan/blue beam color
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("beamParticle", 8, 8);
    graphics.clear();
    graphics.fillStyle(0xffffff, 1); // white core
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("beamParticleWhite", 8, 8);
    graphics.destroy();
  }

  // Create main beam emitter (cyan particles rising up)
  const beamEmitter = scene.add.particles(x, y + 16, "beamParticle", {
    lifespan: 800,
    speed: { min: 80, max: 150 },
    angle: { min: -100, max: -80 }, // Upward direction
    scale: { start: 1.5, end: 0 },
    alpha: { start: 0.8, end: 0 },
    frequency: 20, // Emit continuously
    blendMode: Phaser.BlendModes.ADD,
    gravityY: -50, // Pull upward
  });
  beamEmitter.setDepth(SPLASH_PARTICLE_DEPTH);

  // Create white core emitter (brighter center)
  const coreEmitter = scene.add.particles(x, y + 16, "beamParticleWhite", {
    lifespan: 600,
    speed: { min: 100, max: 180 },
    angle: { min: -100, max: -80 },
    scale: { start: 1, end: 0 },
    alpha: { start: 0.6, end: 0 },
    frequency: 25,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: -50,
  });
  coreEmitter.setDepth(SPLASH_PARTICLE_DEPTH + 1);

  // Create ground ring effect (expanding outward)
  const ringEmitter = scene.add.particles(x, y + 16, "beamParticle", {
    lifespan: 400,
    speed: { min: 40, max: 80 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0.2 },
    alpha: { start: 0.9, end: 0 },
    quantity: 12,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 0,
  });
  ringEmitter.setDepth(SPLASH_PARTICLE_DEPTH);

  // Ensure particles are only visible in the main camera, not the UI camera
  const cameras = scene.cameras.cameras;
  cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(beamEmitter);
      camera.ignore(coreEmitter);
      camera.ignore(ringEmitter);
    }
  });

  // Stop emitters after 1 second and destroy after particles fade
  scene.time.delayedCall(1000, () => {
    beamEmitter.stop();
    coreEmitter.stop();
    ringEmitter.destroy(); // Ring only fires once
  });

  scene.time.delayedCall(1800, () => {
    beamEmitter.destroy();
    coreEmitter.destroy();
  });
}

/**
 * Triggers a large blood splash particle effect at (x, y) for player death.
 * Uses a mix of bright and dark red particles with more quantity and speed.
 */
export function showDeathBloodSplash(
  scene: Phaser.Scene,
  x: number,
  y: number
) {
  // Create textures if not present
  if (!scene.textures.exists("brightRed")) {
    const graphics = scene.add.graphics();
    graphics.fillStyle(0xff0000, 1); // bright red
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("brightRed", 8, 8);
    graphics.clear();
    graphics.fillStyle(0x800000, 1); // dark red
    graphics.fillCircle(4, 4, 4);
    graphics.generateTexture("darkRed", 8, 8);
    graphics.destroy();
  }

  // Create large bright red emitter
  const brightEmitter = scene.add.particles(x, y, "brightRed", {
    lifespan: 400,
    speed: { min: 100, max: 300 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0.6 },
    quantity: 30,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 100,
  });
  brightEmitter.setDepth(SPLASH_PARTICLE_DEPTH);

  // Create large dark red emitter
  const darkEmitter = scene.add.particles(x, y, "darkRed", {
    lifespan: 400,
    speed: { min: 100, max: 300 },
    angle: { min: 0, max: 360 },
    scale: { start: 1, end: 0 },
    alpha: { start: 1, end: 0.6 },
    quantity: 30,
    blendMode: Phaser.BlendModes.ADD,
    gravityY: 100,
  });
  darkEmitter.setDepth(SPLASH_PARTICLE_DEPTH);

  // Ensure particles are only visible in the main camera, not the UI camera
  const cameras = scene.cameras.cameras;
  cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(brightEmitter);
      camera.ignore(darkEmitter);
    }
  });

  // Remove emitters after particles fade
  scene.time.delayedCall(800, () => {
    brightEmitter.destroy();
    darkEmitter.destroy();
  });
}

const CONFETTI_COLORS = [0xff4444, 0x4ade80, 0x7fd8f0, 0xf0c040, 0xffffff];
const CONFETTI_DEPTH = 200000;

/**
 * Rains confetti rectangles down across the full width of the scene — used for
 * celebratory moments (e.g. completing a challenge) rendered over the UI.
 */
export function showConfetti(scene: Phaser.Scene) {
  const { width } = scene.scale;
  const emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

  CONFETTI_COLORS.forEach((color) => {
    const key = `confetti-${color.toString(16)}`;
    if (!scene.textures.exists(key)) {
      const graphics = scene.add.graphics();
      graphics.fillStyle(color, 1);
      graphics.fillRect(0, 0, 6, 10);
      graphics.generateTexture(key, 6, 10);
      graphics.destroy();
    }

    const emitter = scene.add.particles(0, -20, key, {
      x: { min: 0, max: width },
      lifespan: 2200,
      speedY: { min: 150, max: 260 },
      speedX: { min: -60, max: 60 },
      rotate: { min: 0, max: 360 },
      scale: { start: 1, end: 0.8 },
      alpha: { start: 1, end: 0 },
      quantity: 2,
      frequency: 30,
      gravityY: 120,
    });
    emitter.setDepth(CONFETTI_DEPTH);
    emitter.setScrollFactor(0);
    emitters.push(emitter);
  });

  scene.time.delayedCall(900, () => emitters.forEach((e) => e.stop()));
  scene.time.delayedCall(2200, () => emitters.forEach((e) => e.destroy()));
}
